import { ThemeButton } from './ThemeWrapper';
import { LabHostDiskTable, LabHostOverviewTable } from './LabCapacityMetrics';
import { discoveryPercent, formatElapsed } from '../domain/infra/discoveryProgress';

function DiscoveryException({ exception, elapsedMs }) {
  if (!exception?.message) return null;
  return (
    <div
      className="rounded p-3 space-y-1 text-sm"
      style={{ border: '1px solid var(--color-error, #b91c1c)', backgroundColor: 'rgba(185, 28, 28, 0.06)' }}
      role="alert"
    >
      <p className="font-medium text-red-700">Discovery failed</p>
      {exception.code ? (
        <p className="text-red-700">
          Code: <code className="font-mono">{exception.code}</code>
        </p>
      ) : null}
      <p className="text-red-700 whitespace-pre-wrap">{exception.message}</p>
      {elapsedMs >= 1000 ? (
        <p className="text-red-700">Stopped after {formatElapsed(elapsedMs)}</p>
      ) : null}
      {exception.sshUser ? (
        <p className="text-red-700">
          SSH user: <code className="font-mono">{exception.sshUser}</code>
        </p>
      ) : null}
      {exception.keyPath ? (
        <p className="text-red-700">
          Key path: <code className="font-mono">{exception.keyPath}</code>
        </p>
      ) : null}
    </div>
  );
}

function DiscoveryProgress({ running, elapsedMs, progress }) {
  if (!running && progress?.status !== 'running') return null;
  const total = Number(progress?.total || 0);
  const probed = Number(progress?.probed || 0);
  const percent = total ? discoveryPercent({ probed, total }) : 0;
  const elapsed = formatElapsed(progress?.elapsedMs || elapsedMs);

  return (
    <div
      className="p-3 rounded-lg space-y-2"
      style={{ border: '1px solid var(--color-border)' }}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
        <span className="theme-text font-medium">Running discovery</span>
        <span className="theme-text-secondary font-mono text-xs">
          {elapsed}
          {total ? ` · ${probed}/${total}` : ''}
          {total ? ` · ${percent}%` : ''}
        </span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded" style={{ backgroundColor: 'var(--color-border)' }}>
        <div
          className="h-full rounded transition-[width] duration-300 ease-out"
          style={{
            width: `${total ? percent : Math.min(95, 8 + (Number(elapsedMs) || 0) / 800)}%`,
            backgroundColor: 'var(--color-primary, #1f4e79)',
          }}
        />
      </div>
      <p className="text-xs theme-text-secondary">
        {progress?.currentAlias
          ? `Probing ${progress.currentAlias}. If this host hangs, SMS skips it and verifies the next server.`
          : 'Probing catalog lab hosts over SSH. A hung host is skipped so the next server can be verified.'}
      </p>
    </div>
  );
}

function DiscoveryResult({ progress, ingestMessage, elapsedMs }) {
  const result = progress?.result;
  const hostErrors = result?.hostErrors || [];
  if (!ingestMessage && !result && !hostErrors.length) return null;
  const elapsed = formatElapsed(progress?.elapsedMs || elapsedMs);
  return (
    <div className="space-y-2">
      {ingestMessage ? <p className="text-sm theme-text">{ingestMessage}</p> : null}
      {result ? (
        <p className="text-sm theme-text-secondary">
          Last run captured in {elapsed}: {result.sshOk} SSH OK, {result.sshFailed} failed
          {result.discoveredAt ? ` · ${new Date(result.discoveredAt).toLocaleString()}` : ''}
        </p>
      ) : null}
      {hostErrors.length ? (
        <div
          className="rounded p-3 text-sm space-y-1"
          style={{ border: '1px solid var(--color-error, #b91c1c)', backgroundColor: 'rgba(185, 28, 28, 0.06)' }}
          role="alert"
        >
          <p className="font-medium text-red-700">Host exceptions ({hostErrors.length})</p>
          <ul className="list-disc pl-5 space-y-1">
            {hostErrors.map((item) => (
              <li key={item.alias} className="text-red-700">
                <code className="font-mono">{item.alias}</code>
                {item.ssh && item.ssh !== 'FAILED' ? ` [${item.ssh}]` : ''}: {item.error}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

export default function LabCapacityPanel({
  capacity,
  loading,
  error,
  onApplySuggestion,
  ingesting,
  running,
  ingestError,
  ingestMessage,
  onIngestFile,
  onRunDiscovery,
  progress,
  exception,
  elapsedMs,
  extraBusy = false,
}) {
  const suggestion = capacity?.suggestion;
  const hosts = capacity?.hosts || [];
  const busy = Boolean(ingesting || running || extraBusy);

  return (
    <section className="rounded border p-4 space-y-3" style={{ borderColor: 'var(--color-border)' }}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold theme-text">Lab capacity</h2>
          <p className="text-sm theme-text-secondary">
            Counts are SMS-created Linux accounts per catalog host. Run discovery from SMS to probe
            those hosts over lab SSH and fill CPU, RAM, load, uptime, last seen, and local disks.
            If a host is still running past 12s, SMS skips it and verifies the next server. Confirm
            NFS and license before create.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {onRunDiscovery ? (
            <ThemeButton type="button" disabled={busy} onClick={onRunDiscovery}>
              {running ? `Running… ${formatElapsed(elapsedMs)}` : 'Run discovery'}
            </ThemeButton>
          ) : null}
          {suggestion && onApplySuggestion ? (
            <ThemeButton type="button" variant="outline" onClick={() => onApplySuggestion(suggestion)}>
              Use {suggestion.alias} for new users
            </ThemeButton>
          ) : null}
        </div>
      </div>

      {onIngestFile ? (
        <label className="block text-sm theme-text">
          Or upload inventory.csv
          <input
            className="block mt-2 theme-text"
            type="file"
            accept=".csv,text/csv"
            disabled={busy}
            onChange={(e) => {
              const file = e.target.files?.[0] || null;
              e.target.value = '';
              onIngestFile(file);
            }}
          />
        </label>
      ) : null}

      <DiscoveryProgress running={running} elapsedMs={elapsedMs} progress={progress} />
      {ingesting ? <p className="text-sm theme-text-secondary">Ingesting inventory.csv…</p> : null}
      <DiscoveryException exception={exception} elapsedMs={elapsedMs} />
      {!exception && ingestError ? <p className="text-sm text-red-600 whitespace-pre-wrap">{ingestError}</p> : null}
      <DiscoveryResult
        progress={progress}
        ingestMessage={exception ? '' : ingestMessage}
        elapsedMs={elapsedMs}
      />

      {loading ? <p className="text-sm theme-text-secondary">Loading capacity…</p> : null}
      {error ? <p className="text-sm text-red-600">{error}</p> : null}

      {capacity?.discoveredAt && !loading ? (
        <p className="text-sm theme-text-secondary">
          Last discovery: {new Date(capacity.discoveredAt).toLocaleString()}
        </p>
      ) : null}

      {suggestion && !loading ? (
        <p className="text-sm theme-text">
          Suggested host: <strong>{suggestion.alias}</strong> ({suggestion.fqdn}) — {suggestion.reason}
        </p>
      ) : null}

      {hosts.length ? (
        <div className="space-y-4">
          <LabHostOverviewTable hosts={hosts} suggestion={suggestion} discoveredAt={capacity?.discoveredAt} />
          <p className="text-xs theme-text-secondary">
            CPU % is busy time since boot. Load average is the live 1 / 5 / 15 minute view. Disks are
            local mounts only (NFS is omitted so discovery cannot hang on a stale share).
          </p>
          <LabHostDiskTable hosts={hosts} discoveredAt={capacity?.discoveredAt} />
        </div>
      ) : null}
    </section>
  );
}
